import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, UserCog, Search, Check } from 'lucide-react';
import { PseudoManager, PseudoManagerCandidate, PseudoManagerReportee } from '../types/pseudoManager';

interface AssignPseudoManagerModalProps {
  /** The Manager's own direct reports — only these can be picked as a Pseudo Manager. */
  candidatePool: PseudoManagerReportee[];
  /** Employee IDs already active as a Pseudo Manager — excluded from the candidate search. */
  assignedPseudoManagerIds: string[];
  /** Reportees selectable for this assignment (unassigned ones, plus the PM's own when editing). */
  selectablePool: PseudoManagerReportee[];
  /** When provided, the modal edits this Pseudo Manager's reportees instead of creating a new one. */
  existingPseudoManager?: PseudoManager;
  onClose: () => void;
  onSubmit: (candidate: PseudoManagerCandidate, reporteeIds: string[]) => void;
}

export function AssignPseudoManagerModal({
  candidatePool,
  assignedPseudoManagerIds,
  selectablePool,
  existingPseudoManager,
  onClose,
  onSubmit,
}: AssignPseudoManagerModalProps) {
  const isEditing = !!existingPseudoManager;

  const [query, setQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [reporteeQuery, setReporteeQuery] = useState('');
  const [candidate, setCandidate] = useState<PseudoManagerCandidate | null>(
    existingPseudoManager
      ? {
          employeeId: existingPseudoManager.employeeId,
          displayName: existingPseudoManager.displayName,
          email: existingPseudoManager.email,
          jobTitle: existingPseudoManager.jobTitle,
        }
      : null
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(existingPseudoManager?.assignedReportees.map((r) => r.employeeId) ?? [])
  );

  const containerRef = useRef<HTMLDivElement>(null);

  // Candidates are restricted to the Manager's own reportees who aren't already a Pseudo Manager.
  const availableCandidates = useMemo(() => {
    const excluded = new Set(assignedPseudoManagerIds);
    return candidatePool.filter((r) => !excluded.has(r.employeeId));
  }, [candidatePool, assignedPseudoManagerIds]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return availableCandidates;
    return availableCandidates.filter(
      (r) => r.displayName.toLowerCase().includes(q) || r.email.toLowerCase().includes(q)
    );
  }, [query, availableCandidates]);

  const reporteeOptions = useMemo(
    () => selectablePool.filter((r) => r.employeeId !== candidate?.employeeId),
    [selectablePool, candidate]
  );

  const filteredReporteeOptions = useMemo(() => {
    const q = reporteeQuery.trim().toLowerCase();
    if (!q) return reporteeOptions;
    return reporteeOptions.filter(
      (r) => r.displayName.toLowerCase().includes(q) || r.email.toLowerCase().includes(q)
    );
  }, [reporteeQuery, reporteeOptions]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const toggleReportee = (employeeId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  };

  const canSubmit = !!candidate && selectedIds.size >= 0;

  const handleSubmit = () => {
    if (!candidate) return;
    onSubmit(candidate, Array.from(selectedIds));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div className="relative z-10 w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
              <UserCog className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-800 leading-tight">
                {isEditing ? 'Manage Reportees' : 'Assign Pseudo Manager'}
              </h2>
              <p className="text-xs text-slate-500">
                {isEditing ? existingPseudoManager!.displayName : 'Pick a person, then their reportees'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          {!isEditing && (
            <div ref={containerRef} className="relative">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-indigo-600 text-white text-xs font-bold mr-1.5 align-middle">1</span>
                Pseudo Manager
              </label>
              {candidate ? (
                <div className="flex items-center justify-between px-4 py-2.5 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{candidate.displayName}</p>
                    <p className="text-xs text-slate-500">{candidate.email}</p>
                  </div>
                  <button
                    onClick={() => setCandidate(null)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="Search your reportees by name or email…"
                    className="w-full pl-9 pr-9 py-2.5 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-indigo-400 transition-colors"
                  />

                  {showDropdown && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                      {suggestions.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-slate-400 italic">No matching reportees</p>
                      ) : (
                        suggestions.map((s) => (
                          <button
                            key={s.employeeId}
                            onClick={() => {
                              setCandidate(s);
                              setQuery('');
                              setShowDropdown(false);
                            }}
                            className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors"
                          >
                            <p className="text-sm font-medium text-slate-800">{s.displayName}</p>
                            <p className="text-xs text-slate-500">{s.email}{s.jobTitle ? ` · ${s.jobTitle}` : ''}</p>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              {!isEditing && (
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-indigo-600 text-white text-xs font-bold mr-1.5 align-middle">2</span>
              )}
              Reportees ({selectedIds.size} selected)
            </label>
            {!isEditing && !candidate ? (
              <p className="text-sm text-slate-400 italic">Pick a Pseudo Manager first.</p>
            ) : (
              <>
                {reporteeOptions.length > 5 && (
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      value={reporteeQuery}
                      onChange={(e) => setReporteeQuery(e.target.value)}
                      placeholder="Search reportees…"
                      className="w-full pl-9 pr-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-indigo-400 transition-colors"
                    />
                  </div>
                )}
                {reporteeOptions.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No reportees available to assign right now.</p>
                ) : filteredReporteeOptions.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No matching reportees.</p>
                ) : (
                  <div className="space-y-1 max-h-64 overflow-y-auto border border-slate-200 rounded-lg p-1.5">
                    {filteredReporteeOptions.map((r) => {
                      const checked = selectedIds.has(r.employeeId);
                      return (
                        <button
                          key={r.employeeId}
                          onClick={() => toggleReportee(r.employeeId)}
                          className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                            checked ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-slate-50 border border-transparent'
                          }`}
                        >
                          <div>
                            <p className="text-sm font-medium text-slate-800">{r.displayName}</p>
                            <p className="text-xs text-slate-500">{r.jobTitle || r.email}</p>
                          </div>
                          <div
                            className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ${
                              checked ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'
                            }`}
                          >
                            {checked && <Check className="w-3.5 h-3.5 text-white" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            Gets automatic access to Connect, Recruitment and Employee Central.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 pt-3 border-t border-slate-100 flex items-center justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 border border-slate-300 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-100 disabled:text-slate-400 text-white rounded-lg transition-colors"
          >
            {isEditing ? 'Save Changes' : 'Assign Pseudo Manager'}
          </button>
        </div>
      </div>
    </div>
  );
}
